/**
 * HelpContent — topic chips at top, scrollable HTML body below.
 * Mirrors XTS HelpContent.
 */

import DOMPurify from "dompurify";
import type { HelpSection } from "./helpData";

interface HelpContentProps {
  section: HelpSection;
  activeTopic: string;
  onTopicChange: (topicId: string) => void;
}

export function HelpContent({ section, activeTopic, onTopicChange }: HelpContentProps) {
  const currentTopic = section.topics.find((t) => t.id === activeTopic);

  return (
    <div
      id={`tabpanel-${section.id}`}
      role="tabpanel"
      aria-labelledby={`tab-${section.id}`}
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <div
        style={{
          borderBottom: "1px solid var(--border)",
          padding: 12,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {section.topics.map((topic) => {
            const isActive = activeTopic === topic.id;
            return (
              <button
                key={topic.id}
                type="button"
                onClick={() => onTopicChange(topic.id)}
                aria-pressed={isActive}
                data-testid={`help-topic-${topic.id}`}
                style={{
                  padding: "5px 11px",
                  borderRadius: 99,
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: isActive ? "white" : "var(--text-3)",
                  background: isActive ? "var(--accent)" : "var(--bg-soft)",
                  border: `1px solid ${isActive ? "var(--accent)" : "var(--border)"}`,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(topic.title) }}
              />
            );
          })}
        </div>
      </div>

      <div
        className="help-body"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 18,
          fontSize: 13,
          lineHeight: 1.6,
          color: "var(--text-2)",
        }}
      >
        {currentTopic ? (
          <>
            <h3
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: "var(--text)",
                margin: "0 0 12px",
                letterSpacing: "-0.01em",
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentTopic.title) }}
            />
            <div
              data-testid={`help-body-${currentTopic.id}`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentTopic.body) }}
            />
          </>
        ) : (
          <p style={{ color: "var(--text-3)" }}>No topic selected.</p>
        )}
      </div>
    </div>
  );
}
